import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  TrendingUp, Loader2, Play, Pause, Ban, DollarSign, CalendarRange, Rocket,
  RefreshCw, CircleSlash, Users, FlaskConical,
} from "lucide-react";

const STAGES = ["prepared", "pilot", "validation", "growth", "observation", "paused"];
const STAGE_COLOR = {
  prepared: "bg-slate-800 text-slate-300 border-slate-700",
  pilot: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  validation: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  growth: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  observation: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  paused: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function Stat({ label, value }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-600">{label}</p>
      <p className="text-lg font-display font-bold text-slate-100">{value}</p>
    </div>
  );
}

function ControlButton({ active, onToggle, labelOn, labelOff, testid }) {
  return active ? (
    <Button size="sm" variant="outline" onClick={onToggle} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={testid}>
      <Play className="w-4 h-4 mr-1.5" /> {labelOff}
    </Button>
  ) : (
    <Button size="sm" variant="outline" onClick={onToggle} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid={testid}>
      <Pause className="w-4 h-4 mr-1.5" /> {labelOn}
    </Button>
  );
}

export default function Growth() {
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [o, r] = await Promise.all([api.get("/growth/overview"), api.get("/growth/report")]);
      setData(o.data); setReport(r.data);
    } catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPlan = async () => {
    setBusy(true);
    try {
      const forced = !!data.current_batch;
      const { data: b } = await api.post(`/growth/plan/run-now${forced ? "?force=true" : ""}`);
      toast.success(b.idempotent ? b.note : `Weekly plan ${b.week_key} (rev ${b.revision}): ${b.prepared_count} prepared, ${b.admitted.length} admitted`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const control = async (action) => {
    try { const { data: r } = await api.post(`/growth/controls/${action}`); toast.success(`${action} applied${r.cancelled_schedules ? ` (${r.cancelled_schedules} cancelled)` : ""}`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStage = async (cid, stage) => {
    try { await api.put(`/growth/channels/${cid}/stage`, { stage }); toast.success(`Stage → ${stage}`); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const evaluate = async (cid) => {
    try { const { data: d } = await api.post(`/growth/channels/${cid}/evaluate`); toast.message(`${d.decision} → ${d.stage}`, { description: d.evidence?.reason || "" }); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <LoadingState label="Loading growth…" />;

  const { settings, spend, forecast, channels, current_batch: batch, controls, experiments } = data;

  return (
    <div className="animate-in-up" data-testid="growth-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Autopilot</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Weekly Gradual Growth</h1>
          <p className="text-sm text-slate-400 mt-2">Gradual, evidence-gated expansion. Channels age only from first confirmed publication — paused/waiting time never ages a channel. Forecasts are deterministic; no invented revenue.</p>
        </div>
        <Button onClick={runPlan} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="run-weekly-plan">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Rocket className="w-4 h-4 mr-1.5" />} {data.current_batch ? "Re-run plan (new revision)" : "Run weekly plan now"}
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="growth-controls">
        <ControlButton active={controls.expansion_paused} onToggle={() => control(controls.expansion_paused ? "resume-expansion" : "pause-expansion")} labelOn="Pause expansion" labelOff="Resume expansion" testid="control-expansion" />
        <ControlButton active={controls.production_paused} onToggle={() => control(controls.production_paused ? "resume-production" : "pause-production")} labelOn="Pause production" labelOff="Resume production" testid="control-production" />
        <ControlButton active={controls.publishing_stopped} onToggle={() => control(controls.publishing_stopped ? "resume-publishing" : "stop-publishing")} labelOn="Stop publishing" labelOff="Resume publishing" testid="control-publishing" />
        <span className="text-xs text-slate-500 ml-auto">Weekly target: <span className="text-slate-300">{settings.weekly_target}</span> · plan Mon {settings.plan_hour}:00 {settings.timezone}</span>
      </div>

      {/* Spend */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="growth-spend">
        <Stat label="Aggregate cap" value={`$${spend.aggregate_cap}`} />
        <Stat label="Actual spend (mo)" value={`$${spend.actual_spend}`} />
        <Stat label="In-flight" value={`$${spend.in_flight} (${spend.in_flight_jobs})`} />
        <Stat label="Remaining" value={`$${spend.remaining}`} />
      </div>

      {/* Forecast */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-6" data-testid="growth-forecast">
        <h2 className="text-lg font-display font-bold text-slate-100 mb-1 flex items-center gap-2"><CalendarRange className="w-4 h-4 text-violet-300" /> 8-week forecast</h2>
        <p className="text-xs text-slate-500 mb-3">Per-video cost ${forecast.per_video.total} ({Object.entries(forecast.per_video.components).map(([k, v]) => `${k} ${v ?? "unknown"}`).join(", ")}){forecast.unknown_present ? " · some components unknown" : ""}. Deterministic; never funded by hypothetical revenue.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wide text-slate-600 text-left">
              <th className="py-1.5 pr-4">Week</th><th className="py-1.5 pr-4">Videos</th><th className="py-1.5 pr-4">Production</th><th className="py-1.5 pr-4">Launch assets</th><th className="py-1.5 pr-4">New prepared</th><th className="py-1.5">Period cost</th>
            </tr></thead>
            <tbody>
              {forecast.weeks.map((w) => (
                <tr key={w.week} className="border-t border-slate-800/60 text-slate-300" data-testid={`forecast-${w.week}`}>
                  <td className="py-1.5 pr-4 font-mono text-xs">{w.week}</td>
                  <td className="py-1.5 pr-4">{w.scheduled_videos}</td>
                  <td className="py-1.5 pr-4">${w.production_cost}</td>
                  <td className="py-1.5 pr-4">${w.launch_assets_cost}</td>
                  <td className="py-1.5 pr-4">{w.new_prepared}</td>
                  <td className="py-1.5 font-medium text-slate-100">${w.period_cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* This week's batch */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-6" data-testid="growth-batch">
        <h2 className="text-lg font-display font-bold text-slate-100 mb-3">This week ({data.current_week}){batch ? ` · revision ${batch.revision}` : ""}</h2>
        {!batch ? (
          <p className="text-sm text-slate-500">No batch run yet this week — click “Run weekly plan now”.</p>
        ) : (
          <div className="space-y-3">
            {batch.blocked_reason && <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5 text-sm text-rose-300 flex items-center gap-2" data-testid="batch-blocked"><Ban className="w-4 h-4" /> {batch.blocked_reason}</div>}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-slate-800 text-slate-300 border border-slate-700">{batch.prepared_count} prepared packages</span>
              <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{batch.admitted.length} admitted to pilot</span>
              {batch.waves?.map((w, i) => <span key={i} className="px-2 py-1 rounded bg-slate-800 text-slate-400 border border-slate-700">wave {i + 1}: {w.admitted}</span>)}
            </div>
            {batch.waves_reason && (batch.waves?.length || 0) === 0 && <p className="text-xs text-slate-500" data-testid="batch-waves-reason">Waves: {batch.waves_reason}</p>}
            {batch.deferred?.length > 0 && (
              <div data-testid="batch-deferred">
                {batch.deferred.map((d) => (
                  <p key={d.reason} className="text-xs text-amber-300/80 flex items-start gap-1.5"><CircleSlash className="w-3.5 h-3.5 mt-0.5 flex-none" /> {d.slots} slot(s) deferred — {d.reason}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Channels */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-6" data-testid="growth-channels">
        <h2 className="text-lg font-display font-bold text-slate-100 mb-3">Channels</h2>
        <div className="space-y-2">
          {channels.map((c) => (
            <div key={c.channel_id} className="flex flex-wrap items-center gap-3 bg-slate-900/50 border border-slate-800 rounded-lg px-4 py-2.5" data-testid={`growth-channel-${c.channel_id}`}>
              <span className="text-sm text-slate-200 font-medium min-w-[140px]">{c.name}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${STAGE_COLOR[c.stage]}`} data-testid={`stage-badge-${c.channel_id}`}>{c.stage}</span>
              <span className="text-xs text-slate-500">cadence {c.cadence}/wk · age {c.age_weeks}w</span>
              {c.required_action && <span className="text-xs text-amber-300/80">{c.required_action}</span>}
              <div className="flex items-center gap-2 ml-auto">
                <Select value={c.stage} onValueChange={(v) => setStage(c.channel_id, v)}>
                  <SelectTrigger className="h-8 w-[140px] bg-slate-900 border-slate-700 text-slate-200 text-xs" data-testid={`stage-select-${c.channel_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => evaluate(c.channel_id)} className="border-slate-700 text-slate-300 hover:bg-slate-800 h-8" data-testid={`evaluate-${c.channel_id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Evaluate</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Report + experiments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="growth-report">
          <h2 className="text-lg font-display font-bold text-slate-100 mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-violet-300" /> Weekly report</h2>
          <p className="text-xs text-amber-300/80 mb-3">{report?.audience_note}</p>
          <div className="space-y-1.5">
            {(report?.channels || []).map((r) => (
              <div key={r.channel_id} className="flex items-center justify-between text-sm text-slate-300">
                <span>{r.name}</span><span className="text-xs text-slate-500">{r.stage} · produced {r.produced} · {r.activated ? "activated" : "not activated"}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5" data-testid="growth-experiments">
          <h2 className="text-lg font-display font-bold text-slate-100 mb-2 flex items-center gap-2"><FlaskConical className="w-4 h-4 text-violet-300" /> Experiments</h2>
          <p className="text-xs text-slate-500 mb-3">Bounded learning loop — ≤20% of eligible videos. Each records the hypothesis, what changed, uncertainty and how to undo.</p>
          {experiments.length === 0 ? <p className="text-sm text-slate-600">No experiments yet.</p> : experiments.map((e) => (
            <div key={e.id} className="border border-slate-800 rounded-lg px-3 py-2 mb-2 text-xs text-slate-400">
              <p className="text-slate-200">{e.hypothesis}</p>
              <p>Changed: {e.what_changed} · Undo: {e.how_to_undo}</p>
              <p className="text-amber-300/70">{e.uncertainty}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
